import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControlLabel,
	Switch,
	Typography,
} from '@barracuda-internal/bds-core-v2';
import React, { ReactElement, useEffect, useState } from 'react';
import AccountRegionSwitcher from 'src/components/AccountRegionSwitcher';
import FEATURES from 'src/featureMap';
import { useApiRequest } from 'src/hooks/useApi';
import { useAppToolchain } from 'src/hooks/useAppToolchain';
import useAuth from 'src/hooks/useAuth';
import withAnalytics from 'src/lib/withAnalytics';
import { OnboardingStepProps } from 'src/routes/onboarding/types';
import mixpanel from 'mixpanel-browser';
import { version } from '../../../package.json';

interface Props extends OnboardingStepProps {}

function DataSetup({ setStep, activeStep }: Props): ReactElement {
	const [selectedRegion, setSelectedRegion] = useState<string>();
	const [doSaveRegion, setDoSaveRegion] = useState(false);
	const [consent, setConsent] = useState<boolean>(false);
	const [consentActionDone, setConsentActionDone] = useState(false);

	const { auth } = useAuth();
	const { api } = useAppToolchain();
	const { config } = useAppToolchain();
	const handleRegionChange = withAnalytics(
		FEATURES.onboardingDataLocationChange,
		(region) => setSelectedRegion(region),
		(region) => {
			return { region };
		},
	);
	const systemConfig = useAppToolchain().config;

	const handleConsentChange = async (
		e: React.ChangeEvent<HTMLInputElement>,
		checked: boolean,
	) => {
		setConsent(checked);
		if (!consentActionDone) {
			setConsentActionDone(true);
		}
	};

	const handleNext = () => {
		const regionUrl = systemConfig.regionMapping[selectedRegion];

		const url = window.location.href;
		if (selectedRegion == 'IN') {
			if (!url.includes(regionUrl)) {
				window.location.replace('https://' + regionUrl);
			}
		}
		if (
			selectedRegion.toLowerCase() != config.currentRegion &&
			config.regionMapping[selectedRegion.toLowerCase()] != undefined
		) {
			setOpenRedirectWarn(true);
		} else {
			setDoSaveRegion(true);
		}
	};

	const [updateStatus, res, err] = useApiRequest(
		doSaveRegion &&
			!!api &&
			!!auth &&
			((signal) =>
				api.saveAccountConfig(
					auth.accountDetails.account.id,
					[
						{
							path: '/storageRegion',
							op: 'replace',
							value: selectedRegion,
						},
						{
							path: '/enableMalwareScan',
							op: 'replace',
							value: consent,
						},
					],
					{ signal },
				)),
		[doSaveRegion, api, auth, selectedRegion, consent],
		([status, res, err]) => {
			setDoSaveRegion(false);

			if (err) {
				localStorage.removeItem('malwareScanConsent');
				return;
			}

			// Set localStorage flag on success
			localStorage.setItem('malwareScanConsent', JSON.stringify(consent));

			if (openRedirectWarn) {
				location.href =
					'https://' +
					config.regionMapping[selectedRegion.toLowerCase()];
			}

			setStep(activeStep + 1);
			logConsentChangeinMixpanel();
		},
	);
	const [openRedirectWarn, setOpenRedirectWarn] = useState(false);

	const logConsentChangeinMixpanel = () => {
		if (consentActionDone) {
			const action = FEATURES.settingsMalwareScanningToggle;
			mixpanel.track(`DIN ${action.id}`, {
				urlPath: location.pathname,
				'Malware Scan': consent ? 'enabled' : 'disabled',
				feature: action.feature,
				appVersion: version,
				'Account Id': auth?.accountDetails.account.id,
				'Account Name': auth?.accountDetails.account.name,
			});
		}
	};

	// Effects
	useEffect(() => {
		const storedConsent = localStorage.getItem('malwareScanConsent');
		if (storedConsent !== null) {
			setConsent(JSON.parse(storedConsent));
		} else {
			setConsent(false); // Default if not present
		}
	}, []);

	return (
		<>
			<Dialog open={openRedirectWarn}>
				<DialogTitle>
					<Typography variant="h2">Confirm Redirect</Typography>
				</DialogTitle>
				<DialogContent>
					<Alert severity="warning" sx={{ marginBottom: '24px' }}>
						As the selected region is different from your existing
						site region, we are going to redirect to{' '}
						{selectedRegion != undefined
							? config.regionMapping[selectedRegion.toLowerCase()]
							: ''}
					</Alert>
				</DialogContent>
				<DialogActions>
					<Button
						bdsType="interactiveSubtle"
						onClick={() => setOpenRedirectWarn(false)}
					>
						Cancel
					</Button>
					<Button
						isLoading={updateStatus === 'RUNNING'}
						onClick={() => {
							setDoSaveRegion(true);
						}}
					>
						Continue
					</Button>
				</DialogActions>
			</Dialog>
			<Box>
				{err ? (
					<Alert severity="error" sx={{ marginTop: '32px' }}>
						An error occurred while saving the configuration.
						Please reload the page and try again.
					</Alert>
				) : null}
				<Typography
					variant="h1"
					color="textPrimary"
					sx={{ marginTop: err ? '16px' : '32px' }}
				>
					Configuration
				</Typography>

				<Box sx={{ marginTop: '16px' }}>
					<Alert severity="info">
						Note: You always have the option to modify your
						configuration later through the Settings.
					</Alert>
				</Box>
					<Box sx={{ marginTop: '24px' }}>
						<Typography
							variant="h3"
							color="textPrimary"
							sx={{ marginBottom: '16px' }}
						>
							Select data storage region
						</Typography>
						<Typography variant="body1" color="textPrimary">
							Data Inspector stores your organization&#39;s and
							tenant information according to your region,
							ensuring compliance with storage-related regulatory
							policies.
						</Typography>
					</Box>

					<Box sx={{ marginTop: '16px' }}>
						<AccountRegionSwitcher
							onChange={handleRegionChange}
							onBoarding={true}
						></AccountRegionSwitcher>
					</Box>

					<Divider sx={{ marginTop: '24px' }} />

					<Box sx={{ marginTop: '24px' }}>
						<Typography
							variant="h3"
							color="textPrimary"
							sx={{ marginBottom: '16px' }}
						>
							Enable Malware Scanning
						</Typography>

						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: '24px',
							}}
						>
							<FormControlLabel
								control={
									<Switch
										name="checkedB"
										color="primary"
										onChange={handleConsentChange}
										checked={consent}
									/>
								}
								label={consent ? 'Enabled' : 'Enable'}
								sx={{ marginLeft: 0, gap: '8px' }}
							/>
							<Typography
								variant="body1"
								color="textPrimary"
							>
								Enable malware scanning to detect
								malicious files.
							</Typography>
						</Box>
					</Box>

				<Box sx={{ marginTop: '40px', display: 'flex', justifyContent: 'flex-end', gap: '16px' }}>
					<Button
						onClick={() => setStep(activeStep - 1)}
						bdsType="interactiveSubtle"
					>
						Back
					</Button>
					<Button onClick={handleNext}>
						Next
					</Button>
				</Box>
			</Box>
		</>
	);
}

export default DataSetup;
